// src/pages/HMRCCallback.js
import { useEffect, useState, useRef } from 'react'
import { useNavigate }                  from 'react-router-dom'
import { useAuth }                      from '../components/AppContext'
import { exchangeCodeForTokens }        from '../lib/hmrc'
import { supabase }                     from '../lib/supabase'
import { getDeviceId } from '../lib/device'
import { CheckCircle, XCircle }         from 'lucide-react'

export default function HMRCCallback() {
  const { user, profile, loading, loadProfile } = useAuth()
  const navigate                        = useNavigate()
  const [status, setStatus]             = useState('loading')
  const [statusMsg, setStatusMsg]       = useState('Connecting your HMRC account...')
  const [errorMsg, setErrorMsg]         = useState('')
  const processed                       = useRef(false)

  useEffect(() => {
    if (loading) return
    if (processed.current) return
    processed.current = true
    handleCallback()
  }, [loading])

  async function handleCallback() {
    const params   = new URLSearchParams(window.location.search)
    const code     = params.get('code')
    const state    = params.get('state')
    const errParam = params.get('error')

    if (errParam) {
      setErrorMsg(params.get('error_description') || 'Access was denied by HMRC.')
      setStatus('error')
      return
    }

    if (!code) {
      setErrorMsg('No authorisation code received from HMRC. Please try again.')
      setStatus('error')
      return
    }

    const savedState = sessionStorage.getItem('hmrc_oauth_state')
    if (!state || state !== savedState) {
      setErrorMsg('Security check failed. Please try connecting again.')
      setStatus('error')
      return
    }
    sessionStorage.removeItem('hmrc_oauth_state')

    if (!user) {
      sessionStorage.setItem('hmrc_pending_code', code)
      navigate('/login')
      return
    }

    try {
      // Step 1: Exchange code for tokens
      setStatusMsg('Exchanging authorisation code...')
      const tokens = await exchangeCodeForTokens(code)

      if (!tokens.accessToken) {
        throw new Error('No access token received from HMRC. Please try again.')
      }

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Your session expired. Please sign in again.')
      const uid = session.user.id

      // Step 2: Fetch business details and MTD status via our own API route.
      // The browser cannot call HMRC directly (CORS), so this must go server-side.
      setStatusMsg('Fetching your HMRC business details...')
      let businessId = null
      let itsaStatus = null
      try {
        const r = await fetch('/api/hmrc-data', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action:       'business-details',
            accessToken:  tokens.accessToken,
            refreshToken: tokens.refreshToken,
            tokenExpiry:  tokens.expiryTime,
            nino:         profile?.hmrc_nino || '',
            userId:       uid,
            deviceId:     getDeviceId(),
            userAgent:    navigator.userAgent,
            timezone:     Intl.DateTimeFormat().resolvedOptions().timeZone,
            screens:      `width=${screen.width}&height=${screen.height}&scaling-factor=${window.devicePixelRatio || 1}&colour-depth=${screen.colorDepth}`,
            // outerWidth/outerHeight are in screen coordinates and are unaffected by
        // page zoom, so the window can never be reported as wider than the
        // screen. innerWidth inflates when the browser is zoomed out.
        windowSize:   `width=${window.outerWidth}&height=${window.outerHeight}`,
            plugins:      Array.from(navigator.plugins || []).map(p => p.name).join(',').substring(0, 500),
            doNotTrack:   navigator.doNotTrack,
            localIPs:     '',
          }),
        })
        const d = await r.json()
        if (r.ok && d.businessId) {
          businessId = d.businessId
          console.log('HMRC businessId fetched:', businessId)
        } else {
          console.warn('Business details unavailable:', d.error || r.status)
        }
      } catch (bizErr) {
        console.warn('Business details fetch failed (non-fatal):', bizErr.message)
      }

      // Also record the customer's MTD status for the current tax year, where available.
      try {
        if (profile?.hmrc_nino) {
          setStatusMsg('Checking your Making Tax Digital status...')
          const r2 = await fetch('/api/hmrc-data', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action:       'itsa-status',
              accessToken:  tokens.accessToken,
              refreshToken: tokens.refreshToken,
              tokenExpiry:  tokens.expiryTime,
              nino:         profile.hmrc_nino,
              userId:       uid,
              deviceId:     getDeviceId(),
              userAgent:    navigator.userAgent,
              timezone:     Intl.DateTimeFormat().resolvedOptions().timeZone,
              localIPs:     '',
            }),
          })
          const d2 = await r2.json()
          if (r2.ok && d2.status) {
            itsaStatus = d2.status
            console.log('HMRC ITSA status:', itsaStatus)
          }
        }
      } catch (statusErr) {
        console.warn('ITSA status check failed (non-fatal):', statusErr.message)
      }

      // Step 3: Save tokens + businessId to profile
      setStatusMsg('Saving your connection...')
      console.log('Saving tokens for user:', uid)

      const updatePayload = {
        hmrc_access_token:  tokens.accessToken,
        hmrc_refresh_token: tokens.refreshToken,
        hmrc_token_expiry:  tokens.expiryTime,
        hmrc_connected:     true,
        updated_at:         new Date().toISOString(),
      }
      if (businessId) updatePayload.hmrc_business_id  = businessId
      if (itsaStatus) updatePayload.hmrc_itsa_status  = itsaStatus

      const { data: updated, error: updateErr } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', uid)
        .select()
        .single()

      if (updateErr) {
        console.error('Profile update error:', updateErr)
        throw new Error(`Failed to save HMRC connection: ${updateErr.message}`)
      }

      console.log('Profile updated:', {
        hmrc_connected: updated?.hmrc_connected,
        has_token:      !!updated?.hmrc_access_token,
        business_id:    updated?.hmrc_business_id,
        expiry:         updated?.hmrc_token_expiry,
      })

      if (loadProfile) await loadProfile(uid)

      setStatus('success')
      setTimeout(() => navigate('/settings?tab=integrations'), 2000)

    } catch (err) {
      console.error('HMRC callback error:', err)
      setErrorMsg(err.message || 'Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  return (
    <div style={{
      minHeight:'100vh', display:'flex', alignItems:'center',
      justifyContent:'center', flexDirection:'column', gap:20,
      background:'var(--navy)', padding:24,
    }}>
      {status === 'loading' && (
        <>
          <div className="spin" style={{ width:32, height:32 }} />
          <div style={{ fontSize:14, color:'var(--slate)', textAlign:'center' }}>
            {loading ? 'Restoring your session...' : statusMsg}
          </div>
        </>
      )}

      {status === 'success' && (
        <>
          <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--teal-glow)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <CheckCircle size={32} color="var(--teal)" />
          </div>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:20, fontWeight:700, color:'var(--white)', marginBottom:8, fontFamily:'var(--font-display)' }}>
              HMRC connected
            </div>
            <div style={{ fontSize:13, color:'var(--slate)', lineHeight:1.6 }}>
              Quarterly can now submit your quarterly updates directly to HMRC.<br />
              Redirecting you back to Settings...
            </div>
          </div>
        </>
      )}

      {status === 'error' && (
        <>
          <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(255,92,92,.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <XCircle size={32} color="var(--red)" />
          </div>
          <div style={{ textAlign:'center', maxWidth:420 }}>
            <div style={{ fontSize:20, fontWeight:700, color:'var(--white)', marginBottom:8, fontFamily:'var(--font-display)' }}>
              Connection failed
            </div>
            <div style={{ fontSize:13, color:'var(--slate)', marginBottom:24, lineHeight:1.6 }}>
              {errorMsg}
            </div>
            <button className="btn btn-secondary" onClick={() => navigate('/settings?tab=integrations')}>
              Back to Settings
            </button>
          </div>
        </>
      )}
    </div>
  )
}
